'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'
import Link from 'next/link'
import { SiteHeader, SiteFooter } from '@/app/page'
import { PinkBallSlider } from '@/components/landing/PinkBallSlider'

interface FormData {
  // Sina
  first_name: string
  last_name: string
  email: string
  phone: string
  role: string
  // Ettevõte
  company_name: string
  reg_code: string
  website: string
  location: string
  employees: string
  // Vajadus
  invoice_volume: string
  current_software: string
  needs: string
  // Source
  source: string
  // Honeypot
  website_url: string // hidden, must stay empty
}

const INITIAL: FormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  role: '',
  company_name: '',
  reg_code: '',
  website: '',
  location: '',
  employees: '',
  invoice_volume: '',
  current_software: '',
  needs: '',
  source: '',
  website_url: '',
}

export default function ApplyPage() {
  const router = useRouter()
  const [data, setData] = useState<FormData>(INITIAL)
  const [verified, setVerified] = useState(false)
  const [agreed, setAgreed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const update = (key: keyof FormData, value: string) => {
    setData(d => ({ ...d, [key]: value }))
  }

  const isValid =
    data.first_name.trim() &&
    data.last_name.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email) &&
    data.role.trim() &&
    data.company_name.trim() &&
    data.reg_code.trim() &&
    data.needs.trim() &&
    verified &&
    agreed &&
    !data.website_url // honeypot

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isValid) return
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('/api/contact-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, lang: 'et' }),
      })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error(j.error || 'Saatmine ebaõnnestus')
      }
      router.push('/taotlus/aitah')
    } catch (err: any) {
      setError(err.message || 'Tekkis viga')
      setSubmitting(false)
    }
  }

  return (
    <div className="bg-[#EBEBEB] min-h-screen" style={{ fontFamily: 'Inter, sans-serif' }}>
      <SiteHeader lang="et" />

      <main className="pt-32 pb-16 px-6 md:px-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="mb-12 text-center">
            <div className="inline-block mb-6 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.18em]" style={{ background: '#111', color: '#fff' }}>
              Liitumistaotlus
            </div>
            <h1 className="text-[36px] md:text-[52px] leading-[1.1] mb-6 tracking-tight" style={{ fontWeight: 300, color: '#111', letterSpacing: '-0.02em' }}>
              Räägi meile<br /><strong style={{ fontWeight: 700 }}>endast.</strong>
            </h1>
            <p className="text-[15px] md:text-[17px] text-gray-600 max-w-xl mx-auto leading-relaxed" style={{ fontWeight: 300 }}>
              Sassu on selektiivne — me ei võta vastu kõiki taotlusi. Täida vorm ja vastame esimesel võimalusel.
              Mida täpsemalt sa kirjutad, seda parem.
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-12">
            {/* Honeypot — hidden from real users, bots fill it */}
            <input
              type="text"
              name="website_url"
              value={data.website_url}
              onChange={e => update('website_url', e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', width: 0, height: 0, opacity: 0 }}
              aria-hidden="true"
            />

            {/* SECTION 1: Sina */}
            <FormSection title="01 — Sinu info">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Eesnimi" required>
                  <Input value={data.first_name} onChange={v => update('first_name', v)} />
                </Field>
                <Field label="Perekonnanimi" required>
                  <Input value={data.last_name} onChange={v => update('last_name', v)} />
                </Field>
              </div>
              <Field label="E-post" required>
                <Input type="email" value={data.email} onChange={v => update('email', v)} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Telefon">
                  <Input type="tel" value={data.phone} onChange={v => update('phone', v)} />
                </Field>
                <Field label="Roll ettevõttes" required>
                  <Input value={data.role} onChange={v => update('role', v)} placeholder="Omanik, raamatupidaja, …" />
                </Field>
              </div>
            </FormSection>

            {/* SECTION 2: Ettevõte */}
            <FormSection title="02 — Sinu ettevõte">
              <Field label="Ettevõtte nimi" required>
                <Input value={data.company_name} onChange={v => update('company_name', v)} />
              </Field>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field label="Registrikood" required>
                  <Input value={data.reg_code} onChange={v => update('reg_code', v)} placeholder="8 numbrit" />
                </Field>
                <Field label="Veebileht">
                  <Input value={data.website} onChange={v => update('website', v)} placeholder="https://" />
                </Field>
              </div>
              <Field label="Asukoht (linn)">
                <Input value={data.location} onChange={v => update('location', v)} />
              </Field>
              <Field label="Töötajate arv">
                <Select value={data.employees} onChange={v => update('employees', v)} options={[
                  { v: '', l: 'Vali…' },
                  { v: '1', l: '1 (üksinda)' },
                  { v: '2-5', l: '2–5' },
                  { v: '6-20', l: '6–20' },
                  { v: '21-50', l: '21–50' },
                  { v: '50+', l: '50+' },
                ]} />
              </Field>
            </FormSection>

            {/* SECTION 3: Vajadus */}
            <FormSection title="03 — Sinu vajadus">
              <Field label="Arvete maht kuus">
                <Select value={data.invoice_volume} onChange={v => update('invoice_volume', v)} options={[
                  { v: '', l: 'Vali…' },
                  { v: '<10', l: 'Alla 10' },
                  { v: '10-50', l: '10–50' },
                  { v: '50-200', l: '50–200' },
                  { v: '200+', l: 'Üle 200' },
                ]} />
              </Field>
              <Field label="Mida kasutad praegu? (kui kasutad)">
                <Textarea value={data.current_software} onChange={v => update('current_software', v)} rows={2} placeholder="Näiteks Smart, Merit, Excel, midagi muud…" />
              </Field>
              <Field label="Mida otsid Sassust?" required>
                <Textarea value={data.needs} onChange={v => update('needs', v)} rows={4} placeholder="Mis on see, mis sind siia tõi? Mida sa praegu ei saa, mida tahaksid?" />
              </Field>
              <Field label="Kuidas meist kuulsid?">
                <Select value={data.source} onChange={v => update('source', v)} options={[
                  { v: '', l: 'Vali…' },
                  { v: 'tuttav', l: 'Tuttav soovitas' },
                  { v: 'google', l: 'Google' },
                  { v: 'linkedin', l: 'LinkedIn' },
                  { v: 'instagram', l: 'Instagram' },
                  { v: 'facebook', l: 'Facebook' },
                  { v: 'muu', l: 'Mujalt' },
                ]} />
              </Field>
            </FormSection>

            {/* SECTION 4: Verifitseerimine + nõusolek */}
            <FormSection title="04 — Kinnitus" last>
              <div className="space-y-4">
                <PinkBallSlider onVerified={() => setVerified(true)} />

                <label className="flex items-start gap-3 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={e => setAgreed(e.target.checked)}
                    className="mt-1 w-4 h-4 rounded border-gray-300 text-black focus:ring-black"
                  />
                  <span className="text-[13px] text-gray-600 leading-relaxed">
                    Olen lugenud ja nõustun{' '}
                    <Link href="/privaatsus" target="_blank" className="underline hover:text-black">privaatsuspoliitikaga</Link>
                    {' '}ja{' '}
                    <Link href="/tingimused" target="_blank" className="underline hover:text-black">tingimustega</Link>.
                  </span>
                </label>
              </div>
            </FormSection>

            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 text-red-700 text-[13px] border border-red-100">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={!isValid || submitting}
              className="w-full px-7 py-4 rounded-full text-[14px] font-semibold transition-all"
              style={{
                background: isValid && !submitting ? '#111' : '#ccc',
                color: '#fff',
                cursor: isValid && !submitting ? 'pointer' : 'not-allowed',
              }}
            >
              {submitting ? 'Saadan…' : 'Esita taotlus →'}
            </button>

            <p className="text-[12px] text-gray-400 text-center mt-4">
              Vastame esimesel võimalusel — tavaliselt 1–3 tööpäeva jooksul.
            </p>
          </form>
        </div>
      </main>

      <SiteFooter lang="et" />
    </div>
  )
}

// ── Form helpers ──────────────────────────────────────────────────
function FormSection({ title, children, last }: { title: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div className={last ? 'mb-8' : 'mb-10 pb-10 border-b border-black/5'}>
      <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-gray-400 mb-5">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  )
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-[12px] font-medium text-gray-700 mb-1.5">
        {label} {required && <span className="text-[#D4878F]">*</span>}
      </div>
      {children}
    </label>
  )
}

function Input({ value, onChange, type = 'text', placeholder }: {
  value: string
  onChange: (v: string) => void
  type?: string
  placeholder?: string
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-[14px] focus:outline-none focus:border-black transition-colors"
    />
  )
}

function Textarea({ value, onChange, rows = 3, placeholder }: {
  value: string
  onChange: (v: string) => void
  rows?: number
  placeholder?: string
}) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-[14px] focus:outline-none focus:border-black transition-colors resize-none"
    />
  )
}

function Select({ value, onChange, options }: {
  value: string
  onChange: (v: string) => void
  options: { v: string; l: string }[]
}) {
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="w-full px-4 py-3 rounded-xl border border-black/10 bg-white text-[14px] focus:outline-none focus:border-black transition-colors appearance-none"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 16px center',
        backgroundSize: '16px',
        paddingRight: '40px',
      }}
    >
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  )
}
